"use client";
import React, { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "motion/react";
import { ChevronDown } from "lucide-react";
import AnimatedCard from "@/components/ui/animated-card";
import BorderWrapper from "@/components/ui/border-wrapper";

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
} as const;

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
} as const;

interface FAQItem {
  question: string;
  answer: string;
}

const FAQRow = ({ item, isLast }: { item: FAQItem; isLast: boolean }) => {
  const [open, setOpen] = useState(false);

  return (
    <motion.div variants={itemVariants} className={isLast ? "" : "border-b border-white/[0.06]"}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-[16px] py-[20px] md:py-[24px] px-[24px] md:px-[32px] lg:px-[40px] text-left cursor-pointer group"
      >
        <h3 className="text-[15px] md:text-[16px] lg:text-[17px] font-medium leading-[1.4] text-foreground/90 group-hover:text-white transition-colors duration-200">
          {item.question}
        </h3>
        <ChevronDown
          className={`w-[18px] h-[18px] shrink-0 text-foreground/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <p className="px-[24px] md:px-[32px] lg:px-[40px] pb-[20px] md:pb-[24px] text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[700px]">
              {item.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

interface FAQSectionProps {
  title: string;
  description?: string;
  items: FAQItem[];
}

const FAQSection = ({ title, description, items }: FAQSectionProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "0px 0px -80px 0px" });

  return (
    <section ref={sectionRef} className="section pt-[20px] lg:pt-[40px] pb-[80px] lg:pb-[140px] relative">
      <div className="container relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
        >
          <motion.div className="text-center mb-[32px] lg:mb-[48px]" variants={itemVariants}>
            <h2 className="text-[28px] md:text-[40px] lg:text-[48px] font-semibold leading-[1.1] mb-[16px]">
              {title}
            </h2>
            {description && (
              <p className="text-[15px] lg:text-[16px] font-light leading-[1.7] text-foreground/90 max-w-[500px] mx-auto">
                {description}
              </p>
            )}
          </motion.div>

          <motion.div variants={itemVariants} className="max-w-[760px] mx-auto">
            <AnimatedCard
              className="rounded-[18px] lg:rounded-[22px]"
              circleClassName="w-[150px] h-[150px] blur-[80px]"
            >
              <BorderWrapper
                wrapperClass="rounded-[18px] lg:rounded-[22px]"
                className="rounded-[18px] lg:rounded-[22px] bg-gradient-to-b from-[#121124] to-[#0e0821] relative overflow-hidden"
              >
                <div className="absolute top-[-10%] right-[10%] w-[300px] h-[300px] bg-theme/[0.05] rounded-full blur-[80px] pointer-events-none animate-shape-pulse" />
                <div className="relative z-10">
                  {items.map((item, i) => (
                    <FAQRow key={i} item={item} isLast={i === items.length - 1} />
                  ))}
                </div>
              </BorderWrapper>
            </AnimatedCard>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
